import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CandidateDashboardLayout from '../components/layouts/CandidateDashboardLayout';
import Icon from '../components/atoms/Icon';
import SessionManager from '../utils/sessionManager';

const API_URL = process.env.REACT_APP_API_URL || 'https://aptirix-version-610696728606.asia-south1.run.app/api';

const CandidateCoursesPage = () => {
  const navigate = useNavigate();
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [courseProgress, setCourseProgress] = useState({}); // { courseId: { overall_completion_percentage, completed_modules, total_modules, ... } }
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const session = SessionManager.getSession('candidate');
    if (!session) {
      navigate('/login');
      return;
    }
    loadCourses();
  }, [navigate]);

  const loadCourses = async () => {
    try {
      setLoading(true);
      const session = SessionManager.getSession('candidate');
      const token = session?.access_token;
      const candidateId = session?.user_id || session?.id;

      const response = await fetch(`${API_URL}/course-teaching/my-courses`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        const courses = data.courses || [];
        setEnrolledCourses(courses);

        // Fetch progress for all courses in parallel
        if (courses.length > 0 && candidateId) {
          const progressMap = {};
          await Promise.all(
            courses.map(async (course) => {
              try {
                const progRes = await fetch(
                  `${API_URL}/course-teaching/progress/summary/${course.id}?candidate_id=${candidateId}`,
                  { headers: { 'Authorization': `Bearer ${token}` } }
                );
                if (progRes.ok) {
                  progressMap[course.id] = await progRes.json();
                }
              } catch (err) {
                console.warn(`Failed to fetch progress for course ${course.id}:`, err);
              }
            })
          );
          setCourseProgress(progressMap);
        }
      } else {
        console.error('Failed to fetch enrolled courses:', response.status);
        setEnrolledCourses([]);
      }
    } catch (error) {
      console.error('Error loading courses:', error);
      setEnrolledCourses([]);
    } finally {
      setLoading(false);
    }
  };

  // Calculate aggregate stats from real progress data
  const completedCourses = enrolledCourses.filter(c => {
    const p = courseProgress[c.id];
    return p && p.total_modules > 0 && p.completed_modules >= p.total_modules;
  }).length;

  const avgProgress = enrolledCourses.length > 0
    ? Math.round(
        enrolledCourses.reduce((sum, c) => {
          const p = courseProgress[c.id];
          return sum + (p?.overall_completion_percentage || 0);
        }, 0) / enrolledCourses.length
      )
    : 0;

  if (loading) {
    return (
      <CandidateDashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
        </div>
      </CandidateDashboardLayout>
    );
  }

  return (
    <CandidateDashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">My Courses</h1>
          <p className="text-gray-600 mt-1">Continue your learning journey</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
            <p className="text-2xl font-bold text-gray-900">{enrolledCourses.length}</p>
            <p className="text-sm text-gray-600">Enrolled Courses</p>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
            <p className="text-2xl font-bold text-gray-900">{completedCourses}</p>
            <p className="text-sm text-gray-600">Completed</p>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
            <p className="text-2xl font-bold text-gray-900">{avgProgress}%</p>
            <p className="text-sm text-gray-600">Avg Progress</p>
          </div>
        </div>

        {/* Courses Grid */}
        {enrolledCourses.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <Icon name="school" size={64} className="text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Courses Enrolled</h3>
            <p className="text-gray-600 mb-6">Start learning by enrolling in courses</p>
            <button
              onClick={() => navigate('/courses')}
              className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
            >
              Browse Courses
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {enrolledCourses.map((course) => {
              const progress = courseProgress[course.id];
              const pct = Math.round(progress?.overall_completion_percentage || 0);
              const completed = progress?.completed_modules || 0;
              const total = progress?.total_modules || 0;

              return (
                <div key={course.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-xs font-medium">
                        {course.category}
                      </span>
                      {pct >= 100 ? (
                        <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                          Completed
                        </span>
                      ) : (
                        <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                          Enrolled
                        </span>
                      )}
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">{course.title}</h3>
                    <div className="flex items-center gap-3 text-sm text-gray-600 mb-4">
                      <span className="flex items-center gap-1">
                        <Icon name="schedule" size={16} />
                        {course.duration}
                      </span>
                      <span className="flex items-center gap-1">
                        <Icon name="signal_cellular_alt" size={16} />
                        {course.level}
                      </span>
                    </div>
                    <div className="mb-4">
                      <div className="flex items-center justify-between text-sm mb-2">
                        <span className="text-gray-600">Progress {total > 0 ? `(${completed}/${total} modules)` : ''}</span>
                        <span className="font-semibold text-teal-600">{pct}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-teal-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${pct}%` }}
                        ></div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <button
                        onClick={() => navigate(`/candidate/courses/${course.id}/progress`)}
                        className="w-full px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center justify-center gap-2"
                      >
                        <Icon name="trending_up" size={16} />
                        View Progress
                      </button>
                      <button
                        onClick={() => navigate(`/courses/${course.id}`)}
                        className="w-full px-4 py-2 text-teal-600 border border-teal-600 rounded-lg hover:bg-teal-50"
                      >
                        Course Details
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </CandidateDashboardLayout>
  );
};

export default CandidateCoursesPage;
